// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * The domain taxonomy the alarm list and the Asset Directory both filter by
 * (EnergyFluo v1 design, ui-mocks/EnergyFluo v1.html: `domainForAlarm` and the
 * directory's `assetDomainPills`). One taxonomy for both, so a domain chip on
 * Alarms names the same assets as the same chip in the directory.
 */

import { stripOntologyPrefix } from './entityHelpers';

/** Display order, the design's: alarm domains, with Compute where assets put it. */
export const DOMAIN_ORDER = [
  'Electrical',
  'Mechanical',
  'Liquid Cooling',
  'Compute',
  'Environmental',
  'Fire/Life Safety',
  'Controls/BMS',
  'Security',
] as const;

export type AssetDomain = (typeof DOMAIN_ORDER)[number] | 'Other';

/** Rank for sorting chips; unknown domains last. */
export function domainRank(domain: string): number {
  const i = (DOMAIN_ORDER as readonly string[]).indexOf(domain);
  return i < 0 ? DOMAIN_ORDER.length : i;
}

// First match wins, so the narrow domains are tested before the broad ones:
// a coolant distribution unit is Liquid Cooling before it is Mechanical, an IT
// rack PDU is Electrical (it is a PDU), a battery room Electrical.
const RULES: [AssetDomain, RegExp][] = [
  ['Liquid Cooling', /liquid|coolant|(^|[^a-z])cdu([^a-z]|$)/],
  ['Fire/Life Safety', /fire|smoke|vesda|suppress|life safety/],
  ['Security', /security|access control|camera|cctv/],
  ['Controls/BMS', /bms|controller|gateway|plc|scada|niagara|controls?([^a-z]|$)|device/],
  [
    'Electrical',
    /ups|pdu|rpp|switch|breaker|transformer|(^|[^a-z])(ats|sts)([^a-z]|$)|generator|feeder|bus|panel|battery|bess|meter|utility|interconnection|electric|power/,
  ],
  [
    'Mechanical',
    /chiller|crah|crac|(^|[^a-z])ahu([^a-z]|$)|air handl|pump|cool|fan|damper|evaporator|hvac|rooftop|doas|tank|tower|heat exchanger|mechanical|aisle/,
  ],
  ['Environmental', /environ|humid|leak|zone/],
  ['Compute', /rack|server|compute|data ?hall|(^|[^a-z])it([^a-z]|$)/],
];

/** Domain for free text: an entity type, a display type, an equipment_type. */
export function domainOfText(text: string | null | undefined): AssetDomain {
  const t = (text ?? '').replace(/_/g, ' ').toLowerCase();
  if (!t) return 'Other';
  return RULES.find(([, re]) => re.test(t))?.[0] ?? 'Other';
}

/** Domain of a digital twin entity, from its ontology type ("UPSType", "Air_Cooled_Chiller"). */
export function domainOfEntityType(rawType: string): AssetDomain {
  return domainOfText(stripOntologyPrefix(rawType).replace(/Type$/, ''));
}

/** AEM rule-id prefixes: rules are named "<DOMAIN>-<class>-<n>", e.g. ELE-A-006. */
const RULE_PREFIX: Record<string, AssetDomain> = {
  ELE: 'Electrical',
  MEC: 'Mechanical',
  LIQ: 'Liquid Cooling',
  ENV: 'Environmental',
  FLS: 'Fire/Life Safety',
  CTL: 'Controls/BMS',
  SEC: 'Security',
};

/**
 * An alarm's domain. Its own asset decides first, when the twin holds it: the
 * alarm is about that exact directory item, so it sits under the same chip.
 * Then the rule-id prefix, then AEM's free-text equipment_type.
 */
export function alarmDomain(
  alarm: { ruleId?: string; id?: string; group?: string },
  assetEntityType?: string
): AssetDomain {
  if (assetEntityType) {
    const fromAsset = domainOfEntityType(assetEntityType);
    if (fromAsset !== 'Other') return fromAsset;
  }
  const prefix = (alarm.ruleId ?? alarm.id ?? '').split('-')[0].toUpperCase();
  return RULE_PREFIX[prefix] ?? domainOfText(alarm.group);
}
